import { gql } from '@apollo/client';
import { ProductAmpFragment, ProductBaseDetailFragment, ProductConfigurableInfoFragment } from './fragment';

export const GET_STORE_CONFIG = /* GraphQL */ `
  query getStoreConfig {
    storeConfig {
      store_code
      product_url_suffix
      grid_per_page
    }
  }
`;

export const GET_CATEGORY_FILTER_OPTIONS = /* GraphQL */ `
  query getCategoryFilterOptions($filter: ProductAttributeFilterInput!) {
    products(filter: $filter) {
      aggregations {
        label
        count
        attribute_code
        options {
          label
          value
          swatch_data {
            value
            thumbnail
          }
          count
          parent_id
          url_key
        }
        position
      }
    }
  }
`;

export const GET_ALL_CATEGORY = /* GraphQL */ `
  query getAllCategory {
    allCategories {
      items {
        id
        uid
        name
        url_key
        is_featured
      }
      aggregations {
        code
        option_id
        rewrite
        label
        swatch_data {
          value
          thumbnail
        }
      }
      sort_fields {
        default
        options {
          label
          value
        }
      }
    }
  }
`;

export const GET_CATEGORY_SEO = /* GraphQL */ `
  query getCategorySeo($id: String!, $layer_filter_items: [String]) {
    categories(filters: { category_uid: { in: [$id] } }, layer_filter_items: $layer_filter_items) {
      items {
        id
        uid
        name
        meta_title
        meta_keywords
        meta_description
      }
    }
  }
`;

export const GET_PRODUCT_SEO_INFO = /* GraphQL */ `
  query getProductSeoInfo($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        id
        meta_title
        meta_keyword
        meta_description
        url_key
        seo_rich_snippets
        image {
          label
          url
        }
        price_range {
          minimum_price {
            discount {
              amount_off
            }
            final_price {
              currency
              value
            }
            regular_price {
              value
              currency
            }
          }
          maximum_price {
            discount {
              amount_off
            }
            final_price {
              currency
              value
            }
            regular_price {
              value
              currency
            }
          }
        }
      }
    }
  }
`;

export const GET_URL_RESOLVER = /* GraphQL */ `
  query getUrlResolver($url: String!) {
    urlResolver(url: $url) {
      id
      redirectCode
      relative_url
      type
      product_type
    }
  }
`;

export const GET_HOME_PAGE_SEO = /* GraphQL */ `
  query getHomePageSeo($identifier: String) {
    cmsPage(identifier: $identifier) {
      meta_description
      meta_keywords
      meta_title
    }
  }
`;

export const GET_HOME_PAGE_SEO_RICH_SNIPPETS = /* GraphQL */ `
  query getHomePageSeoRichSnippets($identifier: String) {
    cmsPage(identifier: $identifier) {
      seo_rich_snippets
    }
  }
`;

export const GET_HOME_CMS_PRODUCT = /* GraphQL */ `
  query getHomeCmsProduct($width: Int!, $height: Int!) {
    CmsProducts(width: $width, height: $height) {
      type
      id
      name
      imgurl
      link
      content
      price
      special_price
      hover_image
      view_all_link
    }
  }
`;
export const GET_HOME_CMS_BANNERS = /* GraphQL */ `
  query getHomeCmsBanners {
    CmsBanners {
      id
      title
      subtitle
      p_pic
      m_pic
      p_video
      m_video
      link
      position
    }
  }
`;
export const GET_BRIDESMAID_DRESSES_COLOR_FILTER = /* GraphQL */ `
  query GetBridesmaidDressColorFilter {
    products(filter: { category_uid: { eq: "NA==" } }) {
      aggregations {
        label
        count
        attribute_code
        options {
          label
          value
          swatch_data {
            value
            thumbnail
          }
          count
        }
        position
      }
    }
    allCategories {
      aggregations {
        code
        option_id
        rewrite
        label
        swatch_data {
          value
          thumbnail
        }
      }
    }
  }
`;

export const GET_STORE_GRID_PER_PAGE = /* GraphQL */ `
  query getStoreGridPerPage {
    storeConfig {
      grid_per_page
    }
  }
`;

export const GET_ATTRIBUTE_FILTER_INPUT = /* GraphQL */ `
  query getAttributeFilterInput {
    __type(name: "ProductAttributeFilterInput") {
      inputFields {
        name
        type {
          name
        }
      }
    }
  }
`;

export const GET_PRODUCT_FILTER_OPTIONS_AND_SORT_FIELDS_BY_SEARCH = /* GraphQL */ `
  query getProductFiltersAndSortMethodsBySearch($search: String!, $filter: ProductAttributeFilterInput!) {
    products(search: $search, filter: $filter) {
      aggregations {
        label
        count
        attribute_code
        options {
          count
          label
          value
          parent_id
          url_key
          swatch_data {
            type
            value
          }
        }
        position
      }
      sort_fields {
        options {
          label
          value
        }
      }
    }
  }
`;

export const PRODUCT_SEARCH = /* GraphQL */ `
  query ProductSearch(
    $currentPage: Int = 1
    $inputText: String!
    $pageSize: Int = 6
    $filters: ProductAttributeFilterInput!
    $sort: ProductAttributeSortInput
    $filter_color: String
  ) {
    products(currentPage: $currentPage, pageSize: $pageSize, search: $inputText, filter: $filters, sort: $sort) {
      items {
        id
        uid
        name
        version
        price_range {
          maximum_price {
            final_price {
              currency
              value
            }
            regular_price {
              currency
              value
            }
            discount {
              amount_off
            }
          }
          minimum_price {
            final_price {
              currency
              value
            }
            regular_price {
              currency
              value
            }
            discount {
              amount_off
            }
          }
        }
        sku
        small_image {
          url(type: "thumbnail", width: 90, height: 132)
        }
        image {
          url(type: "image", width: 692, height: 1016)
        }
        show_colors(width: 692, height: 1016, filter_color: $filter_color) {
          items {
            label
            rgb
            image
            hover_image
            small_image
            small_hover_image
            is_selected
          }
          total
        }
        product_mark
        stock_status
        rating_summary
        review_count
        __typename
        url_key
      }
      page_info {
        total_pages
      }
      total_count
    }
  }
`;

export const GET_CATEGORY_BREADCRUMBS = /* GraphQL */ `
  query GetCategoryBreadcrumbs($category_uid: String!) {
    categories(filters: { category_uid: { eq: $category_uid } }) {
      items {
        breadcrumbs {
          category_uid
          category_level
          category_name
          category_url_key
        }
        uid
        name
        url_key
        __typename
      }
    }
  }
`;

export const GET_CATEGORY_INFO = /* GraphQL */ `
  query getCategoryInfo($uid: String!) {
    categories(filters: { category_uid: { eq: $uid } }) {
      items {
        id
        seo_rich_snippets
        uid
        name
        description
        url_key
        url_path
        category_image
        title_h1
        meta_title
        meta_keywords
        meta_description
        __typename
        available_sort_fields {
          default
          options {
            label
            value
          }
        }
      }
    }
  }
`;

export const GET_CATEGORY_PRODUCTS = /* GraphQL */ `
  query getCategoryProducts(
    $id: String!
    $pageSize: Int!
    $currentPage: Int!
    $filters: ProductAttributeFilterInput!
    $sort: ProductAttributeSortInput
    $filter_color: String
  ) {
    categories(filters: { category_uid: { in: [$id] } }) {
      items {
        id
        uid
        url_key
        adv(currentPage: $currentPage) {
          id
          title
          img
          link
          position
          __typename
        }
        __typename
      }
    }
    products(pageSize: $pageSize, currentPage: $currentPage, filter: $filters, sort: $sort) {
      items {
        id
        uid
        name
        fabrics_mark
        stock_status
        version
        price_range {
          maximum_price {
            final_price {
              currency
              value
            }
            regular_price {
              currency
              value
            }
            discount {
              amount_off
            }
          }
          minimum_price {
            final_price {
              currency
              value
            }
            regular_price {
              currency
              value
            }
            discount {
              amount_off
            }
          }
        }
        sku
        image {
          url(type: "thumbnail", width: 692, height: 1016)
        }
        hover_image(width: 692, height: 1016)
        show_colors(width: 692, height: 1016, category_uid: $id, filter_color: $filter_color) {
          items {
            label
            rgb
            image
            hover_image
            is_selected
            is_change_url
          }
          total
        }
        product_mark
        video_url
        stock_status
        rating_summary
        review_count
        __typename
        url_key
      }
      page_info {
        total_pages
      }

      total_count
    }
  }
`;

export const GET_PRODUCT_PAGE_AVAILABLE_PRODUCT_DETAIL = /* GraphQL */ `
  query getProductPageAvailableProductDetail($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        available_product {
          sku
          uid
          id
          __typename
          rating_summary
          review_count
          categories {
            uid
            name
            url_key
            breadcrumbs {
              category_uid
              category_id
            }
          }
          shipping_type
          video_url
          video_gallery
          ready_to_ship_product_url
          estimate_delivery {
            code
            value
          }
          special_price
          description {
            html
          }
          short_description {
            html
          }
          question_answer {
            author
            email
            question
            answer
            date
          }
          notes
          order_free_swatch {
            label
            href
          }
          media_gallery_entries {
            uid
            label
            position
            disabled
            file
          }
          media_gallery {
            label
            url(width: 90, height: 132)
            thumbnail: url(type: "thumbnail", width: 90, height: 132)
            image: url(type: "image", width: 692, height: 1016)
            position
            disabled
          }
          image_gallery {
            imgUrl
          }
          name
          price {
            regularPrice {
              amount {
                currency
                value
              }
            }
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
              discount {
                amount_off
              }
              regular_price {
                currency
                value
              }
            }
            minimum_price {
              final_price {
                currency
                value
              }
              regular_price {
                currency
                value
              }
              discount {
                amount_off
              }
            }
          }
          show_attribute {
            label
            code
            id
            value
          }
          small_image {
            url(type: "thumbnail", width: 90, height: 132)
          }
          stock_status
          product_mark
          url_key
          ... on ConfigurableProduct {
            configurable_options {
              attribute_code
              attribute_id
              uid
              label
              product_id
              position
              values {
                uid
                default_label
                label
                store_label
                use_default_value
                value_index
                swatch_data {
                  ... on ImageSwatchData {
                    thumbnail
                  }
                  value
                }
              }
            }
            variants {
              attributes {
                code
                value_index
              }
              product {
                id
                uid
                media_gallery_entries {
                  uid
                  disabled
                  file
                  label
                  position
                }
                media_gallery {
                  label
                  url(width: 90, height: 132)
                  thumbnail: url(type: "thumbnail", width: 90, height: 132)
                  image: url(type: "image", width: 692, height: 1016)
                  position
                  disabled
                }
                sku
                stock_status
                price {
                  regularPrice {
                    amount {
                      currency
                      value
                    }
                  }
                }
                price_range {
                  maximum_price {
                    final_price {
                      currency
                      value
                    }
                    discount {
                      amount_off
                    }
                    regular_price {
                      currency
                      value
                    }
                  }
                  minimum_price {
                    final_price {
                      currency
                      value
                    }
                    regular_price {
                      currency
                      value
                    }
                    discount {
                      amount_off
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_PRODUCT_PAGE_SIMPLE_PRODUCT_DETAIL = /* GraphQL */ `
  query getProductPageSimpleProductDetail($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        ...ProductBaseDetailFragment
      }
    }
  }
  ${ProductBaseDetailFragment}
`;

export const GET_KEY_CHAINS = /* GraphQL */ `
  query getKeyChains($sku: [String]) {
    NotVisibleProducts(sku: $sku) {
      id
      uid
      version
      change_style
      __typename
      categories {
        uid
        name
        url_key
        breadcrumbs {
          category_uid
          category_id
        }
      }
      shipping_type
      video_url
      video_gallery
      ready_to_ship_product_url
      estimate_delivery {
        code
        value
      }
      special_price
      description {
        html
      }
      short_description {
        html
      }
      notes
      is_try_on
      try_on_map_sku
      robes_gifts_customization
      order_free_swatch {
        label
        href
      }
      media_gallery_entries {
        uid
        label
        position
        disabled
        file
      }
      media_gallery {
        label
        url(width: 90, height: 132)
        thumbnail: url(type: "thumbnail", width: 90, height: 132)
        image: url(type: "image", width: 692, height: 1016)
        position
        disabled
      }
      image_gallery {
        imgUrl
      }
      name
      sku
      show_attribute {
        label
        code
        id
        value
      }
      small_image {
        url(type: "thumbnail", width: 90, height: 132)
      }
      stock_status
      product_mark
      url_key
    }
  }
`;

export const GET_PRODUCT_PAGE_TEMPLATE_PRODUCT_DETAIL = /* GraphQL */ `
  query getProductPageTemplateProductDetail($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        ...ProductBaseDetailFragment
        ...ProductAmpFragment
      }
    }
  }
  ${ProductBaseDetailFragment}
  ${ProductAmpFragment}
`;

export const GET_PRODUCT_PAGE_CONFIGURABLE_PRODUCT_DETAIL = /* GraphQL */ `
  query getProductPageConfigurableProductDetail($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        ...ProductBaseDetailFragment
        ...ProductConfigurableInfoFragment
      }
    }
  }
  ${ProductBaseDetailFragment}
  ${ProductConfigurableInfoFragment}
`;

export const GET_COLOR_MENUS = /* GraphQL */ `
  query getColorMenus {
    bridesmaids: products(filter: { category_uid: { eq: "NA==" } }) {
      aggregations {
        attribute_code
        options {
          label
          swatch_data {
            value
            thumbnail
          }
        }
      }
    }
    guest: products(filter: { category_uid: { eq: "NDU=" } }) {
      aggregations {
        attribute_code
        options {
          label
          swatch_data {
            value
            thumbnail
          }
        }
      }
    }
    moms: products(filter: { category_uid: { eq: "MTQ=" } }) {
      aggregations {
        attribute_code
        options {
          label
          swatch_data {
            value
            thumbnail
          }
        }
      }
    }
    dresses: products(filter: { category_uid: { eq: "NTQ=" } }) {
      aggregations {
        attribute_code
        options {
          label
          swatch_data {
            value
            thumbnail
          }
        }
      }
    }
  }
`;

export const GET_ALL_REVIEWS_RICH_SNIPPETS = /* GraphQL */ `
  query getAllReviewsRichSnippets {
    allReviews {
      seo_rich_snippets
    }
  }
`;

export const GET_CMS_BLOCKS = /* GraphQL */ `
  query getCmsBlock($identifiers: [String]!) {
    cmsBlocks(identifiers: $identifiers) {
      items {
        content
        identifier
      }
    }
  }
`;

export const GET_PRODUCT_UPSELL_PRODUCTS = /* GraphQL */ `
  query getProductUpsellProducts($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        __typename
        id
        uid
        url_key
        upsell_products {
          __typename
          id
          uid
          name
          version
          price {
            regularPrice {
              amount {
                currency
                value
              }
            }
          }
          url_key
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
              discount {
                amount_off
              }
              regular_price {
                currency
                value
              }
            }
            minimum_price {
              final_price {
                currency
                value
              }
              regular_price {
                currency
                value
              }
              discount {
                amount_off
              }
            }
          }
          show_colors(width: 692, height: 1016) {
            items {
              label
              rgb
              image
              hover_image
              small_image
              small_hover_image
              is_selected
            }
            total
          }
          image {
            url(type: "thumbnail", width: 692, height: 1016)
          }
          hover_image(width: 692, height: 1016)
          small_image {
            url(type: "thumbnail", width: 90, height: 132)
          }
        }
      }
    }
  }
`;

export const GET_PRODUCT_RELATED_PRODUCTS = /* GraphQL */ `
  query getProductRelatedProduct($urlKey: String!, $filter_color: String) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        id
        uid
        url_key
        related_products {
          __typename
          id
          uid
          name
          version
          price {
            regularPrice {
              amount {
                currency
                value
              }
            }
          }
          url_key
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
              discount {
                amount_off
              }
              regular_price {
                currency
                value
              }
            }
            minimum_price {
              final_price {
                currency
                value
              }
              regular_price {
                currency
                value
              }
              discount {
                amount_off
              }
            }
          }
          show_colors(width: 692, height: 1016, filter_color: $filter_color) {
            items {
              label
              rgb
              image
              hover_image
              small_image
              small_hover_image
              is_selected
            }
            total
          }
          image {
            url(type: "thumbnail", width: 692, height: 1016)
          }
          hover_image(width: 692, height: 1016)
          small_image {
            url(type: "thumbnail", width: 90, height: 132)
          }
        }
      }
    }
  }
`;

export const GET_CMS_ANNOUNCEMENTS = /* GraphQL */ `
  query getCmsAnnouncements {
    CmsAnnouncements {
      id
      title
      content
      link
      position
    }
  }
`;

export const GET_SHOP_BY_COLOR_AGGREGATIONS = /* GraphQL */ `
  query getShopByColorAggregations {
    allCategories {
      aggregations {
        code
        option_id
        rewrite
        label
        swatch_data {
          value
          thumbnail
        }
      }
    }
  }
`;

export const GET_FALL_BRIDESMAID_DRESSES_FILTER = /* GraphQL */ `
  query getFallBridesmaidDressesFilter($filter: ProductAttributeFilterInput!) {
    products(filter: $filter) {
      aggregations {
        attribute_code
        options {
          label
          default_label
          value
          swatch_data {
            value
            thumbnail
          }
        }
      }
    }
    allCategories {
      aggregations {
        code
        option_id
        rewrite
      }
    }
  }
`;

export const GET_ANNIVERSARY_PRIZE_DRAW_NUMBER = gql`
  query getAnniversaryPrizeDrawNumber($email: String!) {
    getAnniversaryPrizeDrawNumber(email: $email) {
      number
    }
  }
`;

export const GET_ANNIVERSARY_PRIZE_WON_DATA = gql`
  query getAnniversaryPrizeWonData($email: String!) {
    getAnniversaryPrizeWonData(email: $email) {
      code
    }
  }
`;
